import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAllPacks, getCardsByPack } from "../db";
import type { Pack } from "../domain/types";
import { MODES } from "../domain/enums";
import { Button } from "../components/Button";

interface Row {
  pack: Pack;
  cardsTotal: number;
  cardsUk: number;
  cardsRu: number;
  cardsEn: number;
}

export default function AdminPacks() {
  const [rows, setRows] = useState<Row[]>([]);

  useEffect(() => {
    (async () => {
      const packs = await getAllPacks();
      const enriched = await Promise.all(
        packs.map(async (p) => {
          const cards = await getCardsByPack(p.id);
          return {
            pack: p,
            cardsTotal: cards.length,
            cardsUk: cards.filter((c) => c.language === "uk").length,
            cardsRu: cards.filter((c) => c.language === "ru").length,
            cardsEn: cards.filter((c) => c.language === "en").length,
          };
        }),
      );
      enriched.sort((a, b) => b.pack.sortPriority - a.pack.sortPriority);
      setRows(enriched);
    })();
  }, []);

  const byMode = MODES.map((m) => ({
    mode: m,
    rows: rows.filter((r) => r.pack.modeId === m.id),
  }));

  return (
    <div className="flex flex-col gap-5 pb-6">
      {/* Top-level entry point — kept above per-mode sections so the host
          finds it without scrolling. */}
      <Link to="/admin/pack/new">
        <Button variant="primary" fullWidth>
          + Нова колода
        </Button>
      </Link>

      {byMode.map(({ mode, rows: list }) => (
        <section key={mode.id}>
          <div className="flex items-center gap-2 mb-2">
            <h3 className="font-display font-bold uppercase tracking-wider text-sm opacity-80">
              {mode.id}
            </h3>
            {mode.isAdult && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/30 text-red-200">
                18+
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {list.map((r) => (
              <div
                key={r.pack.id}
                className="rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 active:scale-[0.99] transition flex items-stretch"
              >
                <Link
                  to={`/admin/pack/${r.pack.id}`}
                  className="flex-1 px-4 py-3 flex items-center gap-3"
                >
                  <div className="flex-1">
                    <div className="font-semibold flex items-center gap-2">
                      {r.pack.titleByLang.uk}
                      {r.pack.userCreated && (
                        <span
                          className="text-[9px] uppercase tracking-wider rounded bg-amber-500/25 text-amber-100 border border-amber-400/40 px-1.5 py-0.5"
                          title="Чернетка — ще не в public/content/. Натисни Export → закинь файли в public/content/ → закомітьте → редеплой → всі гравці побачать."
                        >
                          📝 чернетка
                        </span>
                      )}
                    </div>
                    <div className="text-xs opacity-60 tabular-nums mt-0.5">
                      UK {r.cardsUk} · RU {r.cardsRu} · EN {r.cardsEn}
                    </div>
                  </div>
                  <div className="text-white/50">→</div>
                </Link>
                <Link
                  to={`/admin/pack/${r.pack.id}/edit-pack`}
                  className="px-3 flex items-center text-xs text-white/60 hover:text-white border-l border-white/10"
                  title="Редагувати назву / опис колоди"
                >
                  ⚙
                </Link>
              </div>
            ))}
            {list.length === 0 && (
              <div className="text-xs opacity-50 italic">No packs in this mode yet.</div>
            )}
          </div>
        </section>
      ))}
    </div>
  );
}
